/**
 * The work's insurance, read off its history — OND-48.
 *
 * DERIVED, AND THAT IS THE WHOLE TICKET. OND-48's first plan was a plain
 * editable insurer field on the work; the 28 September decision replaced it with
 * "build the main-page display from OND-95's history model rather than shipping a
 * separate plain-field version", and this file is that sentence. Every period
 * here is a `provenance_entries` row of kind `insurance`, with the confirmations
 * and disputes the whole registry uses, so the panel and the Artwork history tab
 * cannot disagree about the same policy — there is only one record and this is a
 * second reading of it.
 *
 * NO FETCH AND NO ENDPOINT. `useWorkController` already loads the chain to draw
 * the record's tabs, so the periods are computed from rows the screen is holding.
 * A function in SQL would have meant a second round trip to answer a question
 * already in memory, and a `drop` + `create` of `ondo.work_record()` — which
 * every workspace's artwork record reads — to add columns a one-row function
 * cannot hold anyway, since cover is many rows.
 */

import { formatMoney } from "@ondo/shared";
import type { ProvenanceEntry } from "@ondo/shared";

import { plural } from "../lib/text.ts";
import { describeDateRange } from "../provenance/provenance.dates.ts";
import type { ProvenanceItem } from "../provenance/provenance.draft.ts";
import { confirmationStrip } from "../provenance/provenance.verification.ts";
import type { ConfirmationStrip } from "../provenance/provenance.verification.ts";
import { INSURANCE_COPY } from "./insurance.constants.ts";

/** One period of cover, as the panel renders it. */
export interface InsurancePeriod {
  /** The entry this was read off. The row links to it with `?entry=`. */
  entryId: string;
  insurer: string;
  /** Null when the record does not name one — rendered as a sentence, not a dash. */
  policy: string | null;
  /** '2019–2024', '2024–Present', 'Until 2024', 'Date unknown'. */
  period: string;
  /** Formatted with its currency, or null. Never a bare figure. */
  amount: string | null;
  /**
   * The same strip the timeline card wears, from the same counts.
   *
   * Reused rather than recomputed, so one dispute takes this row out of green
   * here exactly as it does there. A panel that worked out its own verdict is
   * the way a confirmation ends up masking a dispute on one surface and not the
   * other.
   */
  strip: ConfirmationStrip;
}

export type InsurancePanel =
  | { kind: "hidden" }
  | {
      kind: "listed";
      periods: InsurancePeriod[];
      /** Said above the list when more than one period stands. */
      note: string | null;
      /** The prominent dispute line, or null when nothing is disputed. */
      alert: { lead: string; detail: string } | null;
    };

/**
 * The panel, from the chain the screen already has.
 *
 * HIDDEN WHEN NOTHING IS RECORDED, which is `toAgentPanel`'s choice for
 * `toAgentPanel`'s reason: the aside is a column of facts about the object, and
 * a heading over "nothing" is a row a reader scrolls past. The door to recording
 * a period is the Insurance chip under "+ Add history", which is on the page
 * already — so an empty panel would offer nothing the screen does not have.
 *
 * WITHDRAWN PERIODS ARE LEFT OUT, and only here. A withdrawn entry stays
 * readable for ever — `readable_provenance_entry_ids()` has no `ended_at` filter
 * and that is what makes "never delete; end" true — but a period somebody
 * withdrew is not cover, and presenting it on the main page as though it were
 * would be the display asserting something the record does not. It is still in
 * the Artwork history, where what was withdrawn and when is the point.
 *
 * NEWEST FIRST, and by RECORDED order rather than by date. `startText` is text
 * — '2019', 'June 2019', 'c. 2019' — so there is nothing to sort on that would
 * not sometimes be wrong, and a panel that put a policy in the wrong year
 * because it could not parse 'by 1971' would be worse than one that shows the
 * renewal first because the renewal was filed last.
 */
export function toInsurancePanel(items: ProvenanceItem[]): InsurancePanel {
  // A type predicate rather than a bare boolean, so `item.entry` is a
  // ProvenanceEntry below and the four insurance fields are reachable without a
  // cast. A draft is excluded for the reason the record's rows exclude one: a
  // half-typed policy is not cover.
  const live = items.filter(
    (item): item is Extract<ProvenanceItem, { kind: "recorded" }> =>
      item.kind === "recorded" &&
      item.entry.kind === "insurance" &&
      item.entry.endedAt === null,
  );

  if (live.length === 0) return { kind: "hidden" };

  const periods = live.map((item) => toPeriod(item)).reverse();

  const disputed = periods.filter((period) => period.strip.tone === "disputed").length;

  return {
    kind: "listed",
    periods,
    note: periods.length > 1 ? INSURANCE_COPY.overlapNote : null,
    alert:
      disputed === 0
        ? null
        : {
            lead: plural(disputed, INSURANCE_COPY.disputedNouns),
            detail: INSURANCE_COPY.disputedDetail,
          },
  };
}

function toPeriod(item: Extract<ProvenanceItem, { kind: "recorded" }>): InsurancePeriod {
  const entry = item.entry;

  return {
    entryId: entry.id,
    // `insurer_name` is NOT NULL on this kind in the database
    // (`provenance_entries_insurance_shape`), so `??` is unreachable rather
    // than a default — the wire field is nullable because the other eight
    // kinds do not carry it.
    insurer: entry.insurerName ?? "",
    policy: entry.policyReference,
    period: describeDateRange(entry.startText ?? "", entry.endText ?? ""),
    amount: toAmount(entry),
    strip: confirmationStrip(item),
  };
}

/**
 * The sum insured, with its currency or not at all.
 *
 * Both or neither, which is the rule the whole repository keeps: "money carries
 * its currency and is never added across currencies". A figure on a record read
 * by somebody in another country, with no code beside it, is worse than saying
 * the amount is not recorded — and the database allows a currency with no sum,
 * which is the case this returns null for.
 */
function toAmount(entry: ProvenanceEntry): string | null {
  const { insuredMinor: minor, insuredCurrency: currency } = entry;
  if (minor === null || currency === null) return null;
  return formatMoney({ minor, currency });
}
